import {makeStyles} from "@mui/styles";
import React, {useState} from "react";
import StyledTreeItem from "../general/tree/StyledTreeItem";
import {MinusSquare, PlusSquare} from "./utils/CustomIcons";
import {Box, Button, Divider, Skeleton, Typography} from "@mui/material";
import {TreeView} from "@mui/lab";
import {NODE_TYPE, TEXT_NODE_VIEW, useTree} from "./useTree";
import {makeRequest, subState} from "../../services/requestService";
import {useSnackbar} from "notistack";
import {API_URL} from "../../constants";
import Line from "./Line";
import Region from "./Region";
import StandardDialogSlide from "../general/dialog/StandardDialogSlide";
import {useTranslation} from "react-i18next";
import {useRecoilValue} from "recoil";
import {useHistory, useLocation} from "react-router-dom";

const useStyles = makeStyles((theme) => ({
    divider: {
        background: theme.palette.secondary.main,
    },
    title: {
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        marginRight: theme.spacing(1),
    }
}));

function TreeSkeleton({page}) {
    const classes = useStyles();
    return (
        <TreeView
            defaultCollapseIcon={<MinusSquare/>}
            defaultExpandIcon={<PlusSquare/>}
            expanded={["content-root"]}
        >
            <StyledTreeItem
                nodeId="content-root"
                label={
                    <>
                        <Typography variant="h6">{page.viewing.split(".")[0]}</Typography>
                        <Divider className={classes.divider}/>
                    </>
                }
                name={`content-root`}
            >
                <Box sx={{width: 350, cursor: "wait"}}>
                    <Skeleton animation="wave" height={30}/>
                    <Skeleton animation="wave" height={30}/>
                    <Skeleton animation="wave" height={30}/>
                    <Skeleton animation="wave" height={30}/>
                </Box>
            </StyledTreeItem>
        </TreeView>
    );
}

export default function TreeDataViewer(props) {
    const classes = useStyles();
    const {
        page,
        issue,
        renderOSD,
        textMode,
        role
    } = props;
    const {enqueueSnackbar} = useSnackbar();
    const {t} = useTranslation("common");
    const sub = useRecoilValue(subState);
    const history = useHistory();
    const location = useLocation();
    // line props
    const [openDeleteDialog, setOpenDeleteDialog] = React.useState(null);
    // region props
    const [openSaveDialog, setOpenSaveDialog] = useState(null);
    // tree props
    const [expanded, setExpanded] = React.useState(["content-root"]);
    const [focusedNodeID, setFocusedNodeID] = React.useState("content-root");
    const {
        treeData,
        reset,
        reload,
        findNodeByID,
        getPathTabOfNode,
        toggleEditState,
        handleChangeViewMode,
        onValueChange
    } = useTree(issue, page, textMode);

    React.useEffect(() => {
        if (sub === "readonly")
            reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sub]);

    React.useEffect(() => { // Update tree from URL
        if (treeData.label !== "loading") {
            const query = new URLSearchParams(location.search);
            const queryOpenNodes = query.getAll("open");
            let pathTab = [];
            const queryFocusedNode = query.get("focused");
            if (queryFocusedNode) {
                pathTab = getPathTabOfNode(treeData, queryFocusedNode, []);
                if (pathTab.length > 0)
                    setFocusedNodeID(queryFocusedNode);
            }
            let setOfNodes = new Set([...pathTab, ...queryOpenNodes]);
            if (setOfNodes.size > 0)
                setExpanded(Array.from(setOfNodes).filter(nodeID => !nodeID.startsWith(NODE_TYPE.LINE)));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [treeData.label]);

    React.useEffect(() => { // Update URL from tree
        if (treeData.label !== "loading")
            history.replace(`${location.pathname}?focused=${focusedNodeID}&open=${expanded.join("&open=")}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expanded, focusedNodeID, treeData.label]);

    React.useEffect(() => { // Print bounding box of the focused element
        if (treeData.label !== "loading") {
            let elem = document.getElementById(focusedNodeID);
            if (elem) {
                renderOSD(elem);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusedNodeID, treeData.label]);

    // handle event

    // line
    const handleInsertLine = (node, isAbove = false) => {
        insertLine(node.data.readingOrder, node.parent.data.id, isAbove).then(() => reload());
    }

    const handleDeleteLine = (node) => {
        deleteLine(node.data.id).then(() => reload());
    }

    const handleRecalculateLineBB = (node) => {
        if (node.type === NODE_TYPE.TEXT)
            recalculateBoundingBox(node.data.id).then(() => reload());
    }

    // text

    const handleSave = (node) => {
        saveText(node);
        if (node.editable)
            toggleEditState(node);
    }

    // actions

    const saveText = (node) => {
        if (node) {
            const save = async () => {
                let error = false;
                let count = 0;
                for (const child of node.children) {
                    if (!error && child.label !== child.data.cachedContent) {
                        const data = {lineId: child.data.id, newContent: child.label};
                        const response = await makeRequest(`${API_URL}/correction`, 'POST', data);
                        if (response.status >= 200 && response.status < 400) {
                            ++count;
                        } else {
                            return Promise.reject(new Error());
                        }
                    }
                }
                return count;
            }
            save().then(count => {
                if (count > 0)
                    enqueueSnackbar(t("main.components.dataViewer.notification.save", {
                        count: count,
                        s: count > 1 ? "s" : ""
                    }), {variant: "success"});
                reload();
            }).catch(() => {
                enqueueSnackbar(t("main.components.dataViewer.notification.savefailure"), {variant: "error"});
                reload().then(() => reset());
            });
        }
    }

    const insertLine = async (index, textID, isAbove) => {
        let url;
        if (isAbove)
            url = `${API_URL}/line?isAbove=true`;
        else
            url = `${API_URL}/line`;
        const data = {index: index, textID: textID}
        const response = await makeRequest(url, 'POST', data);
        if (response.status >= 400) {
            if (response.data)
                enqueueSnackbar(response.data.message, {variant: "error"});
            return Promise.reject(new Error());
        }
        enqueueSnackbar(response.data.message, {variant: "success"});
        return Promise.resolve();
    }

    const deleteLine = async (lineID) => {
        const response = await makeRequest(`${API_URL}/line/${lineID}`, 'DELETE');
        if (response.status !== 200) {
            if (response.data)
                enqueueSnackbar(response.data.message, {variant: "error"});
            return Promise.reject(new Error());
        }
        enqueueSnackbar(response.data.message, {variant: "success"});
        return Promise.resolve();
    }

    const recalculateBoundingBox = async (textID) => {
        const response = await makeRequest(`${API_URL}/text/${textID}/update/lines`, 'PUT');
        if (response.status >= 400) {
            if (response.data)
                enqueueSnackbar(response.data.message, {variant: "error"});
            return Promise.reject(new Error());
        }
        enqueueSnackbar(response.data.message, {variant: "success"});
        return Promise.resolve();
    }

    // tree event

    const handleKeyDown = (event) => {
        event.preventDefault();
        let elem = document.getElementById(focusedNodeID);
        let node = findNodeByID(treeData, focusedNodeID);
        let type = focusedNodeID.split("-")[0];
        switch (type) {
            case NODE_TYPE.CONTENT:
                break;
            case NODE_TYPE.TEXT:
                switch (event.which) {
                    case 69: // e || E Edit
                        if (node && !node.editable && !event.repeat && event.ctrlKey) {
                            toggleEditState(node);
                        }
                        break;
                    case 83: // s || S  Save
                        if (node && node.editable && !event.repeat && event.ctrlKey) {
                            setOpenSaveDialog(node);
                        }
                        break;
                    default:
                    // rien
                }
                break;
            case NODE_TYPE.LINE:
                switch (event.which) {
                    case 13: // Enter  Focus input
                        if (elem) {
                            let input = elem.querySelector("textarea");
                            if (input)
                                input.focus();
                        }
                        break;
                    case 45: // Insert
                        if (node && node.parent.editable && !event.repeat) {
                            if (event.ctrlKey)
                                handleInsertLine(node, true);
                            else
                                handleInsertLine(node);
                        }
                        break;
                    case 46: // Delete
                        if (node && node.parent.editable && !event.repeat)
                            setOpenDeleteDialog(node);
                        break;
                    case 67: // c || C  Copy
                        if (node && !event.repeat && event.ctrlKey) {
                            navigator.clipboard.writeText(node.label).then(function () {
                                /* clipboard successfully set */
                                enqueueSnackbar(t("main.components.dataViewer.notification.copy"), {variant: "success"});
                            }, function () {
                                /* clipboard write failed */
                                enqueueSnackbar(t("main.components.dataViewer.notification.copyfailure"), {variant: "error"});
                            });
                        }
                        break;
                    case 68: // d || D : Delete
                        if (node && node.parent.editable && !event.repeat && event.ctrlKey)
                            setOpenDeleteDialog(node);
                        break;
                    case 69: // e || E Edit
                        if (node && !node.parent.editable && !event.repeat && event.ctrlKey) {
                            toggleEditState(node.parent);
                        }
                        break;
                    case 76: // l || L : Add Line
                        if (node && node.parent.editable && !event.repeat) {
                            if (event.ctrlKey) {
                                if (event.shiftKey)
                                    handleInsertLine(node, true);
                                else
                                    handleInsertLine(node);
                            }
                        }
                        break;
                    case 83: // s || S  Save
                        if (node && node.parent.editable && !event.repeat && event.ctrlKey) {
                            setOpenSaveDialog(node.parent);
                        }
                        break;
                    case 86: // v || V Paste
                        if (node && node.parent.editable && !event.repeat && event.ctrlKey) {
                            navigator.clipboard.readText().then(clipText => {
                                onValueChange(node, node.label + clipText);
                            });
                        }
                        break;
                    default:
                    // nothing
                }
                break;
            default:
            // nothing
        }
        // for all types
        switch (event.which) {
            case 38:
                if (elem) {
                    let next = elem.previousElementSibling;
                    if (next)
                        next.focus();
                }
                break;
            case 40:
                if (elem) {
                    let next = elem.nextElementSibling;
                    if (next)
                        next.focus();
                }
                break;
            case 9: // Tab  Focus next or previous line
                if (elem) {
                    let next;
                    if (event.shiftKey)
                        next = elem.previousElementSibling;
                    else
                        next = elem.nextElementSibling;
                    if (next)
                        next.focus();
                }
                break;
            default:
            // nothing
        }
    }

    // render

    const renderTree = (node) => {
        const globalProps = {
            key: node.id,
            nodeId: node.id,
            id: node.id,
            boundingbox: node.boundingBox,
            factor: node.factor
        }
        let label;
        switch (node.type) {
            case NODE_TYPE.ROOT:
                label =
                    <>
                        <Typography variant="h6" className={classes.title}>{node.label}</Typography>
                        <Divider className={classes.divider}/>
                    </>
                break;
            case NODE_TYPE.CONTENT:
                label = <Typography variant="body1">{node.label}</Typography>;
                break;
            case NODE_TYPE.TEXT:
                return <Region
                    {...globalProps}
                    node={node}
                    role={role}
                    recalculateLineBB={() => handleRecalculateLineBB(node)}
                    onChangeViewMode={(mode) => handleChangeViewMode(node, mode)}
                    toggleEdit={() => {
                        handleChangeViewMode(node, TEXT_NODE_VIEW.TREE);
                        toggleEditState(node);
                    }}
                    onSave={() => {
                        setOpenSaveDialog(node);
                    }}
                    onAnnotationChange={() => reload()}
                >
                    {Array.isArray(node.children)
                        ? node.children.map((children) => renderTree(children))
                        : null}
                </Region>
            case NODE_TYPE.LINE:
                return <Line
                    {...globalProps}
                    node={node}
                    onValueChange={(newValue) => {
                        onValueChange(node, newValue);
                    }}
                    addLine={(isAbove) => {
                        handleInsertLine(node, isAbove);
                    }}
                    deleteLine={() => {
                        setOpenDeleteDialog(node);
                    }}
                    onInputFocus={() => setFocusedNodeID(node.id)}
                >
                    {/*
                    // you can add components there
                    // for example the correction history of the line
                    <Typography variant="body2">
                        {node.data.tokens.map((token) => (token.textContent + " "))}
                    </Typography>*/}
                </Line>
            default:
                label = <Typography variant="body1">{node.label}</Typography>;
        }
        return (
            <StyledTreeItem
                {...globalProps}
                onContextMenu={(event) => event.preventDefault()}
                label={label}>
                {Array.isArray(node.children)
                    ? node.children.map((children) => renderTree(children))
                    : null}
            </StyledTreeItem>
        );
    };

    if (treeData.label === "loading") return <TreeSkeleton page={page}/>

    return (
        <>
            <TreeView
                defaultCollapseIcon={<MinusSquare/>}
                defaultExpandIcon={<PlusSquare/>}
                selected={focusedNodeID}
                expanded={expanded}
                onNodeToggle={(event, nodeIds) => setExpanded(nodeIds)}
                onNodeFocus={(event, nodeId) => setFocusedNodeID(nodeId)}
                onKeyDown={handleKeyDown}
            >
                {renderTree(treeData)}
            </TreeView>
            <StandardDialogSlide
                // delete line dialog
                open={Boolean(openDeleteDialog)}
                onClose={() => setOpenDeleteDialog(null)}
                actions={
                    [
                        <Button key="dialog-delete-action1" color="secondary" onClick={() => setOpenDeleteDialog(null)}>
                            {t("main.components.button.cancel")}
                        </Button>,
                        <Button key="dialog-delete-action2" color="error" onClick={() => {
                            setOpenDeleteDialog(null);
                            handleDeleteLine(openDeleteDialog);
                        }}>
                            {t("main.components.button.delete")}
                        </Button>
                    ]
                }
                title={t("main.components.dataViewer.dialog.delete.title")}
            >
                {openDeleteDialog && openDeleteDialog.label}
            </StandardDialogSlide>
            <StandardDialogSlide
                // Save dialog
                open={Boolean(openSaveDialog)}
                onClose={() => setOpenSaveDialog(null)}
                actions={[
                    <Button
                        key="dialog-save-action1"
                        onClick={() => {
                            reset();
                            toggleEditState(openSaveDialog);
                            setOpenSaveDialog(null);
                        }} color="error">
                        {t("main.components.saveDialog.discard")}
                    </Button>,
                    <Button key="dialog-save-action2" onClick={() => setOpenSaveDialog(null)} color="secondary">
                        {t("main.components.saveDialog.close")}
                    </Button>,
                    <Button
                        key="dialog-save-action3"
                        onClick={() => {
                            handleSave(openSaveDialog);
                            setOpenSaveDialog(null);
                        }} color="secondary">
                        {t("main.components.saveDialog.save")}
                    </Button>
                ]}
                title={t("main.components.dataViewer.dialog.save.title")}
            >
                {t("main.components.dataViewer.dialog.save.description")}
            </StandardDialogSlide>
        </>
    );
}